import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { useFolder } from '../../context/FolderContext';

/**
 * FolderItem - Renders a folder in the sidebar with collapse/expand,
 * drag-drop support, and context menu
 */
function FolderItem({
  folder,
  depth = 0,
  onContextMenu,
  onDragOver,
  onDrop,
  searchQuery
}) {
  const location = useLocation();
  const { isFolderExpanded, toggleFolderExpanded, renameFolder, deleteFolder } = useFolder();

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const editInputRef = useRef(null);

  const isExpanded = isFolderExpanded(folder.id);
  const hasChildren = (folder.children && folder.children.length > 0) ||
                      (folder.conversations && folder.conversations.length > 0);

  // Focus input when editing
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [isEditing]);

  // Close context menu on outside click
  useEffect(() => {
    if (showContextMenu) {
      const handleClick = () => setShowContextMenu(false);
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [showContextMenu]);

  // Handle folder click (toggle expand)
  const handleFolderClick = (e) => {
    e.preventDefault();
    toggleFolderExpanded(folder.id);
  };

  // Handle context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  // Handle rename
  const handleStartRename = () => {
    setEditName(folder.name);
    setIsEditing(true);
    setShowContextMenu(false);
  };

  const handleSaveRename = async () => {
    if (editName.trim() && editName.trim() !== folder.name) {
      try {
        await renameFolder(folder.id, editName.trim());
      } catch (err) {
        console.error('Failed to rename folder:', err);
      }
    }
    setIsEditing(false);
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditName(folder.name);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setShowContextMenu(false);
    if (window.confirm(`Delete folder "${folder.name}"? Conversations will be moved out of the folder.`)) {
      try {
        await deleteFolder(folder.id);
      } catch (err) {
        console.error('Failed to delete folder:', err);
      }
    }
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    if (onDragOver) onDragOver(e, folder.id);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (onDrop) onDrop(e, folder.id);
  };

  return (
    <div className="folder-item">
      {/* Folder header */}
      <div
        className={`
          flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer
          transition-colors group
          ${isDragOver
            ? 'bg-decisiv-blue/20 ring-2 ring-decisiv-blue/50'
            : 'hover:bg-gray-200 dark:hover:bg-gray-700'}
        `}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
        onClick={handleFolderClick}
        onContextMenu={handleContextMenu}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Expand/collapse arrow */}
        <button
          className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          onClick={(e) => {
            e.stopPropagation();
            toggleFolderExpanded(folder.id);
          }}
        >
          <svg
            className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Folder icon */}
        <svg
          className={`w-4 h-4 shrink-0 ${isExpanded ? 'text-decisiv-blue' : 'text-gray-400'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {isExpanded ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          )}
        </svg>

        {/* Folder name */}
        {isEditing ? (
          <input
            ref={editInputRef}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleEditKeyDown}
            onBlur={handleSaveRename}
            className="flex-1 text-sm bg-transparent border-b border-decisiv-blue
              text-gray-900 dark:text-white focus:outline-hidden min-w-0"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
            {folder.name}
          </span>
        )}

        {/* Conversation count badge */}
        {folder.conversation_count > 0 && !isEditing && (
          <span className="text-xs text-gray-400 dark:text-gray-500 px-1.5 py-0.5
            bg-gray-200 dark:bg-gray-700 rounded-full">
            {folder.conversation_count}
          </span>
        )}

        {/* Hover actions */}
        {!isEditing && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleStartRename();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
              title="Rename"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400
                hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
              title="Delete"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Context menu */}
      {showContextMenu && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg
            border border-gray-200 dark:border-gray-700 py-1 min-w-[160px]"
          style={{ left: contextMenuPos.x, top: contextMenuPos.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300
              hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            onClick={handleStartRename}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Rename
          </button>
          <button
            className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400
              hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            onClick={handleDelete}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {/* Expanded content */}
      {isExpanded && hasChildren && (
        <div className="folder-children">
          {/* Nested folders */}
          {folder.children?.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              depth={depth + 1}
              onContextMenu={onContextMenu}
              onDragOver={onDragOver}
              onDrop={onDrop}
              searchQuery={searchQuery}
            />
          ))}

          {/* Conversations in folder */}
          {folder.conversations?.map((conv) => (
            <FolderConversationItem
              key={conv.id}
              conversation={conv}
              folderId={folder.id}
              depth={depth + 1}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * FolderConversationItem - Renders a conversation inside a folder
 */
function FolderConversationItem({ conversation, folderId, depth, searchQuery }) {
  const location = useLocation();
  const { removeConversationFromFolder } = useFolder();
  const isActive = location.pathname === `/chat/${conversation.id}`;

  // Handle drag start
  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'conversation',
      conversationId: conversation.id,
      fromFolderId: folderId
    }));
    e.dataTransfer.effectAllowed = 'move';
  };

  // Handle remove from folder
  const handleRemoveFromFolder = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await removeConversationFromFolder(folderId, conversation.id);
    } catch (err) {
      console.error('Failed to remove from folder:', err);
    }
  };

  return (
    <Link
      to={`/chat/${conversation.id}`}
      draggable
      onDragStart={handleDragStart}
      className={`
        flex items-center gap-2 px-2 py-1.5 rounded-lg
        transition-colors group cursor-grab active:cursor-grabbing
        ${isActive
          ? 'bg-decisiv-blue/10 text-decisiv-blue'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}
      `}
      style={{ paddingLeft: `${20 + depth * 12}px` }}
    >
      {/* Chat icon */}
      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>

      {/* Title */}
      <span className="flex-1 text-sm truncate">
        {conversation.title}
      </span>

      {/* Remove from folder button */}
      <button
        onClick={handleRemoveFromFolder}
        className="p-1 opacity-0 group-hover:opacity-100 text-gray-400
          hover:text-gray-600 dark:hover:text-gray-300
          hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-opacity"
        title="Remove from folder"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </Link>
  );
}

export default FolderItem;
